import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Progress, 
  List, 
  Avatar, 
  Tag,
  Space,
  Button,
  DatePicker,
  Select,
  Table
} from 'antd';
import { 
  MailOutlined, 
  EyeOutlined, 
  LinkOutlined,
  UserOutlined,
  RiseOutlined,
  CalendarOutlined,
  BarChartOutlined,
  SendOutlined
} from '@ant-design/icons';
import { emailAnalyticsService } from '../../../services/emailMarketingService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const EmailAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [timeframe, setTimeframe] = useState('30days');

  useEffect(() => {
    fetchAnalytics();
  }, [timeframe]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const stats = await emailAnalyticsService.getEmailAnalytics();
      setAnalytics(stats);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading email analytics...</div>;
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  const campaignColumns = [
    {
      title: 'Campaign',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {record.subject}
          </Text>
        </div>
      )
    },
    {
      title: 'Sent',
      dataIndex: 'sentCount',
      key: 'sentCount',
      render: (count) => <Text strong>{count || 0}</Text>
    },
    {
      title: 'Opens',
      dataIndex: 'openCount',
      key: 'openCount',
      render: (count, record) => {
        const openRate = record.sentCount > 0 ? (count / record.sentCount * 100).toFixed(1) : 0;
        return (
          <div>
            <Text strong>{count || 0}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '0.8rem' }}>
              {openRate}%
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Clicks',
      dataIndex: 'clickCount',
      key: 'clickCount',
      render: (count, record) => {
        const clickRate = record.sentCount > 0 ? (count / record.sentCount * 100).toFixed(1) : 0;
        return (
          <div>
            <Text strong>{count || 0}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '0.8rem' }}>
              {clickRate}%
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          sent: 'green',
          draft: 'default',
          scheduled: 'blue',
          paused: 'orange'
        };
        return (
          <Tag color={colors[status] || 'default'}>
            {status.toUpperCase()}
          </Tag>
        );
      }
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Email Analytics
            </Title>
            <Text type="secondary">
              Track email campaign performance and engagement
            </Text>
          </Col>
          <Col>
            <Space>
              <Select
                value={timeframe}
                onChange={setTimeframe}
                style={{ width: 120 }}
              >
                <Option value="7days">Last 7 days</Option>
                <Option value="30days">Last 30 days</Option>
                <Option value="90days">Last 90 days</Option>
                <Option value="1year">Last year</Option>
              </Select>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
              />
            </Space>
          </Col>
        </Row>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Campaigns"
              value={analytics.totalCampaigns}
              prefix={<MailOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Emails Sent"
              value={analytics.totalSent}
              prefix={<SendOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Open Rate"
              value={analytics.averageOpenRate}
              precision={1}
              suffix="%"
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Click Rate"
              value={analytics.averageClickRate}
              precision={1}
              suffix="%"
              prefix={<LinkOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Performance Overview */}
        <Col xs={24} lg={12}>
          <Card title="Performance Overview">
            <div style={{ padding: '1rem 0' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <Text>Open Rate</Text>
                  <Text strong>{analytics.averageOpenRate.toFixed(1)}%</Text>
                </div>
                <Progress
                  percent={analytics.averageOpenRate}
                  strokeColor="#faad14"
                  showInfo={false}
                />
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <Text>Click Rate</Text>
                  <Text strong>{analytics.averageClickRate.toFixed(1)}%</Text>
                </div>
                <Progress
                  percent={analytics.averageClickRate}
                  strokeColor="#1890ff"
                  showInfo={false}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <Text>Unsubscribe Rate</Text>
                  <Text strong>0.5%</Text>
                </div>
                <Progress
                  percent={0.5}
                  strokeColor="#ff4d4f"
                  showInfo={false}
                />
              </div>
            </div>
          </Card>
        </Col>

        {/* Recent Campaigns */}
        <Col xs={24} lg={12}>
          <Card title="Recent Campaigns">
            <Table
              columns={campaignColumns}
              dataSource={analytics.recentCampaigns}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>
      </Row>

      {/* Email Trends */}
      <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
        <Col xs={24}>
          <Card title="Email Trends">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ color: '#52c41a', margin: 0 }}>
                    +15%
                  </Title>
                  <Text type="secondary">Open rate improvement</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ color: '#1890ff', margin: 0 }}>
                    +8%
                  </Title>
                  <Text type="secondary">Click rate improvement</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ color: '#faad14', margin: 0 }}>
                    -2%
                  </Title>
                  <Text type="secondary">Unsubscribe rate decrease</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <Space>
          <Button type="primary" icon={<BarChartOutlined />}>
            Export Report
          </Button>
          <Button icon={<CalendarOutlined />}>
            Schedule Report
          </Button>
          <Button icon={<RiseOutlined />}>
            View Trends
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default EmailAnalytics;
